<template>
    <div>
        <!-- Header card -->
        <div class="rounded-md shadow overflow-hidden bg-white ring-1 ring-gray-200 p-6 flex flex-col md:flex-row justify-between gap-6">
            <div class="md:w-2/3 space-y-3">
                <h2 class="text-xl font-semibold text-gray-800">Property Documents</h2>
                <p class="text-gray-600">
                    Upload documents and assign tenants access. You can upload here and manage under your documents tab in the top right dropdown.
                </p>
                <p class="text-xs text-gray-500">
                    Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB per file).
                </p>
            </div>
            <div class="md:w-1/3 flex flex-col gap-3 justify-center">
                <template v-if="!isSharedViewOnly">
                    <button type="button" class="primaryButton w-full justify-center" @click="openUploadModal">
                        <font-awesome-icon :icon="faUpload" class="mr-2" />
                        Upload Document
                    </button>
                </template>
                <ToolTip
                    v-else-if="viewOnlyTooltip"
                    :title="viewOnlyTooltip.title"
                    :description="viewOnlyTooltip.description"
                />
            </div>
        </div>

        <!-- Documents Table -->
        <div class="mt-4 bg-white ring-1 ring-gray-200 rounded-md overflow-hidden">
            <div v-if="documents.length === 0" class="p-8 text-center text-gray-500">
                <font-awesome-icon :icon="faFolderOpen" class="text-3xl text-gray-300 mb-2" />
                <p class="text-sm">No documents uploaded yet.</p>
                <p class="text-xs text-gray-400 mt-1">Upload your first document to get started.</p>
            </div>
            <table v-else class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Document</th>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Uploaded</th>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Shared With</th>
                        <th scope="col" class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                    <tr v-for="doc in documents" :key="doc.id">
                        <td class="px-4 py-3">
                            <div class="flex items-center gap-3">
                                <div class="flex-shrink-0 w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <font-awesome-icon :icon="faFileLines" class="text-purple-600" />
                                </div>
                                <div class="min-w-0">
                                    <button
                                        type="button"
                                        @click="downloadDocument(doc)"
                                        class="text-sm font-medium text-purple-700 hover:text-purple-900 underline truncate text-left"
                                    >
                                        {{ doc.title || doc.file_name }}
                                    </button>
                                    <p v-if="doc.title" class="text-xs text-gray-500 truncate">{{ doc.file_name }}</p>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{{ doc.uploaded_at }}</td>
                        <td class="px-4 py-3 text-sm text-gray-600">
                            <span
                                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
                                :class="doc.granted_user_count > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
                            >
                                {{ doc.granted_user_count }} {{ doc.granted_user_count === 1 ? 'user' : 'users' }}
                            </span>
                            <span v-if="doc.granted_user_count > 0" class="ml-2 text-xs text-gray-500">
                                {{ formatGrantedNames(doc.granted_users) }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-right whitespace-nowrap">
                            <button
                                type="button"
                                @click="downloadDocument(doc)"
                                class="text-xs text-gray-600 hover:text-purple-700 font-medium px-3 py-1.5 border border-gray-300 rounded-md hover:border-purple-300 transition-colors"
                            >
                                Download
                            </button>
                            <template v-if="!isSharedViewOnly">
                                <button
                                    type="button"
                                    @click="openGrantModalForDoc(doc)"
                                    class="ml-2 text-xs text-purple-700 hover:text-purple-900 font-medium px-3 py-1.5 border border-purple-300 rounded-md hover:border-purple-400 transition-colors"
                                >
                                    Manage Access
                                </button>
                                <button
                                    type="button"
                                    @click="confirmDelete(doc)"
                                    class="ml-2 text-xs text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-md hover:border-red-400 transition-colors"
                                >
                                    Delete
                                </button>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Upload Modal -->
        <ModalJetstream :show="showUploadModal" :max-width="'lg'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-xl font-bold text-gray-800">Upload Document</h2>
                    <button type="button" @click="closeUploadModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Title <span class="text-gray-400">(optional)</span></label>
                        <input
                            v-model="uploadTitle"
                            type="text"
                            maxlength="255"
                            placeholder="e.g. Move-in Inspection Report"
                            class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                        <p class="text-xs text-gray-500 mt-1">Defaults to the file name if left blank.</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">File</label>
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            @change="handleFileChange"
                            class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2"
                        >
                        <p class="text-xs text-gray-500 mt-1">PDF, JPG, PNG, DOC, DOCX. Max 10MB.</p>
                    </div>

                    <p v-if="uploadError" class="text-xs text-red-600 bg-red-50 p-2 rounded">{{ uploadError }}</p>

                    <div class="flex justify-end gap-3 mt-4">
                        <button type="button" @click="closeUploadModal" class="closeButton">Cancel</button>
                        <button
                            type="button"
                            @click="uploadFile"
                            :disabled="!selectedFile || uploading"
                            class="primaryButton"
                            :class="{ 'opacity-25': !selectedFile || uploading }"
                        >
                            {{ uploading ? 'Uploading...' : 'Upload' }}
                        </button>
                    </div>
                </div>
            </div>
        </ModalJetstream>

        <!-- Manage Access Modal -->
        <ModalJetstream :show="showGrantModal" :max-width="'2xl'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-xl font-bold text-gray-800">Manage Access</h2>
                    <button type="button" @click="closeGrantModal" class="text-gray-500 hover:text-gray-700">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <p class="text-sm text-gray-600 mb-4">
                    You're updating access for
                    <span class="font-semibold">{{ grantDocumentIds.length }}</span>
                    {{ grantDocumentIds.length === 1 ? 'document' : 'documents' }}.
                    Anyone you check below will be able to view and download
                    {{ grantDocumentIds.length === 1 ? 'this document' : 'these documents' }} from their Documents page.
                </p>

                <div v-if="eligibleUsers.length === 0" class="text-sm text-gray-500 bg-gray-50 rounded p-4 text-center">
                    No approved applicants or active tenants for this property yet.
                </div>

                <div v-else class="max-h-72 overflow-y-auto border border-gray-200 rounded">
                    <ul class="divide-y divide-gray-100">
                        <li
                            v-for="user in eligibleUsers"
                            :key="user.id"
                            class="flex items-center justify-between p-3 hover:bg-gray-50"
                        >
                            <label class="flex items-center gap-3 cursor-pointer flex-1">
                                <input
                                    type="checkbox"
                                    :value="user.id"
                                    v-model="grantSelectedUserIds"
                                    class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                >
                                <div>
                                    <p class="text-sm font-medium text-gray-800">{{ user.name }}</p>
                                    <p class="text-xs text-gray-500">{{ user.email }}</p>
                                </div>
                            </label>
                            <div class="flex flex-wrap gap-1">
                                <span
                                    v-for="source in user.sources"
                                    :key="source"
                                    class="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                    :class="sourceBadgeClass(source)"
                                >
                                    {{ source }}
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="mt-4 flex items-start gap-2">
                    <input id="replace-mode" type="checkbox" v-model="grantReplaceMode" class="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500">
                    <label for="replace-mode" class="text-xs text-gray-600">
                        Replace existing access (uncheck users to revoke them). When unchecked, this only adds access.
                    </label>
                </div>

                <p v-if="grantError" class="text-xs text-red-600 bg-red-50 p-2 rounded mt-3">{{ grantError }}</p>

                <div class="flex justify-end gap-3 mt-5 border-t pt-4">
                    <button type="button" @click="closeGrantModal" class="closeButton">Cancel</button>
                    <button
                        type="button"
                        @click="saveGrants"
                        :disabled="grantSaving || eligibleUsers.length === 0"
                        class="primaryButton"
                        :class="{ 'opacity-25': grantSaving || eligibleUsers.length === 0 }"
                    >
                        {{ grantSaving ? 'Saving...' : 'Save Access' }}
                    </button>
                </div>
            </div>
        </ModalJetstream>

        <!-- Delete Confirmation Modal -->
        <ModalJetstream :show="showDeleteModal" :max-width="'md'">
            <div class="bg-white rounded-lg p-6 m-2">
                <h2 class="text-lg font-bold text-gray-800 mb-3">Delete Document</h2>
                <p class="text-sm text-gray-600 mb-5">
                    Delete <span class="font-medium">{{ docToDelete?.title || docToDelete?.file_name }}</span>?
                    Anyone you've shared it with will lose access. This cannot be undone.
                </p>
                <div class="flex justify-end gap-3">
                    <button type="button" @click="cancelDelete" class="closeButton">Cancel</button>
                    <button
                        type="button"
                        @click="deleteDocument"
                        :disabled="deleting"
                        class="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700 focus:outline-none transition"
                        :class="{ 'opacity-25': deleting }"
                    >
                        {{ deleting ? 'Deleting...' : 'Delete' }}
                    </button>
                </div>
            </div>
        </ModalJetstream>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import axios from 'axios'
import ModalJetstream from '@/Jetstream/Modal.vue'
import ToolTip from '@/Shared/ToolTip.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFolderOpen, faUpload, faFileLines } from '@fortawesome/pro-light-svg-icons'

const props = defineProps({
    property: { type: Object, required: true },
    tab: { type: String, default: 'documents' },
    tabData: { type: Object, default: () => ({}) },
    propertyAccess: { type: String, default: 'owner' },
    viewOnlyTooltip: { type: Object, default: null },
})

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')

const documents = computed(() => props.tabData?.shared_documents || [])
const eligibleUsers = computed(() => props.tabData?.eligible_users || [])

// Upload modal
const showUploadModal = ref(false)
const uploading = ref(false)
const uploadError = ref(null)
const uploadTitle = ref('')
const selectedFile = ref(null)
const fileInput = ref(null)

const openUploadModal = () => {
    if (isSharedViewOnly.value) return
    showUploadModal.value = true
}

const handleFileChange = (event) => {
    selectedFile.value = event.target.files[0] || null
    uploadError.value = null
}

const closeUploadModal = () => {
    showUploadModal.value = false
    selectedFile.value = null
    uploadTitle.value = ''
    uploadError.value = null
    if (fileInput.value) fileInput.value.value = ''
}

const uploadFile = async () => {
    if (isSharedViewOnly.value || !selectedFile.value) return
    uploading.value = true
    uploadError.value = null

    const formData = new FormData()
    formData.append('file', selectedFile.value)
    if (uploadTitle.value.trim()) formData.append('title', uploadTitle.value.trim())

    try {
        await axios.post(`/properties/${props.property.id}/shared-documents`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        closeUploadModal()
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        uploadError.value = error.response?.data?.errors?.file?.[0]
            || error.response?.data?.errors?.title?.[0]
            || error.response?.data?.message
            || 'Upload failed. Please try again.'
    } finally {
        uploading.value = false
    }
}

// Delete modal
const showDeleteModal = ref(false)
const docToDelete = ref(null)
const deleting = ref(false)

const confirmDelete = (doc) => {
    if (isSharedViewOnly.value) return
    docToDelete.value = doc
    showDeleteModal.value = true
}

const cancelDelete = () => {
    showDeleteModal.value = false
    docToDelete.value = null
}

const deleteDocument = async () => {
    if (!docToDelete.value) return
    deleting.value = true
    try {
        await axios.delete(`/properties/${props.property.id}/shared-documents/${docToDelete.value.id}`)
        cancelDelete()
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete document.')
    } finally {
        deleting.value = false
    }
}

// Download
const downloadDocument = async (doc) => {
    try {
        const { data } = await axios.get(`/properties/${props.property.id}/documents/${doc.id}/download`)
        if (data?.url) window.open(data.url, '_blank')
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to generate download link.')
    }
}

// Grant modal
const showGrantModal = ref(false)
const grantDocumentIds = ref([])
const grantSelectedUserIds = ref([])
const grantReplaceMode = ref(false)
const grantSaving = ref(false)
const grantError = ref(null)

// Per-row entry point: manage access for a single document.
const openGrantModalForDoc = (doc) => {
    if (isSharedViewOnly.value) return
    openGrantModalForDocs([doc.id])
}

const openGrantModalForDocs = (docIds) => {
    grantDocumentIds.value = [...docIds]
    const targetDocs = documents.value.filter((d) => grantDocumentIds.value.includes(d.id))
    if (targetDocs.length === 1) {
        grantSelectedUserIds.value = (targetDocs[0].granted_users || []).map((u) => u.id)
    } else {
        const idSets = targetDocs.map((d) => new Set((d.granted_users || []).map((u) => u.id)))
        const intersection = idSets.length
            ? [...idSets.reduce((acc, set) => new Set([...acc].filter((id) => set.has(id))))]
            : []
        grantSelectedUserIds.value = intersection
    }
    grantReplaceMode.value = false
    grantError.value = null
    showGrantModal.value = true
}

const closeGrantModal = () => {
    showGrantModal.value = false
    grantDocumentIds.value = []
    grantSelectedUserIds.value = []
    grantError.value = null
}

const saveGrants = async () => {
    if (grantSelectedUserIds.value.length === 0 && !grantReplaceMode.value) {
        grantError.value = 'Pick at least one user, or check "Replace existing access" to revoke all.'
        return
    }
    grantSaving.value = true
    grantError.value = null
    try {
        if (grantSelectedUserIds.value.length === 0 && grantReplaceMode.value) {
            const allGrantedIds = new Set()
            documents.value
                .filter((d) => grantDocumentIds.value.includes(d.id))
                .forEach((d) => (d.granted_users || []).forEach((u) => allGrantedIds.add(u.id)))
            if (allGrantedIds.size > 0) {
                await axios.post(`/properties/${props.property.id}/shared-documents/revoke`, {
                    document_ids: grantDocumentIds.value,
                    user_ids: [...allGrantedIds],
                })
            }
        } else {
            await axios.post(`/properties/${props.property.id}/shared-documents/grant`, {
                document_ids: grantDocumentIds.value,
                user_ids: grantSelectedUserIds.value,
                mode: grantReplaceMode.value ? 'replace' : 'merge',
            })
        }
        closeGrantModal()
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        grantError.value = error.response?.data?.message || 'Failed to update access.'
    } finally {
        grantSaving.value = false
    }
}

const formatGrantedNames = (users) => {
    if (!users || users.length === 0) return ''
    const first = users.slice(0, 2).map((u) => u.name).join(', ')
    return users.length > 2 ? `${first} +${users.length - 2}` : first
}

const sourceBadgeClass = (source) => {
    switch (source) {
        case 'applicant': return 'bg-blue-100 text-blue-700'
        case 'tenant': return 'bg-green-100 text-green-700'
        case 'maintenance': return 'bg-amber-100 text-amber-700'
        default: return 'bg-gray-100 text-gray-700'
    }
}
</script>
